import React, { useRef, useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import ProjectSelector from '../components/ProjectSelector';
import { getMemberRateForMonth } from '../utils/costUtils';
import WhatIfAllocationGrid from '../components/WhatIfAllocationGrid';
import ProjectCosts from '../components/ProjectCosts';
import CostSummary from '../components/CostSummary';
import Charts from '../components/Charts';
import PermissionGuard from '../components/PermissionGuard';
import AccessDenied from '../components/AccessDenied';
import NewOpportunityModal from '../components/NewOpportunityModal';
import WorkTypeBadge from '../components/WorkTypeBadge';
import { WorkScope, workScopeKey, workTypeLabel } from '../utils/workScope';
import { DEFAULT_PROFIT_PERCENT, revenueFromProfitPercent, round2 } from '../utils/profitability';
import { Opportunity, Project, ProjectCost } from '../types/api';
import { AllocationMember } from '../types/allocation';
import { generateMonths } from '../utils/monthUtils';
import { DashboardService, DashboardData } from '../services/dashboardService';
import projectService from '../services/projectService';
import opportunityService from '../services/opportunityService';
import { MemberCostService } from '../services';
import type { MemberCosts } from '../services/memberCostService';
import { useCallback } from 'react';

type TabType = 'allocation' | 'costs';

/**
 * The outer mode of the page: analysing work that already exists in Redmine,
 * versus costing a prospective opportunity that does not exist there yet.
 */
type ScenarioTab = 'existing' | 'opportunities';

const SCENARIO_TABS: { id: ScenarioTab; label: string; note: string }[] = [
  {
    id: 'existing',
    label: 'What-If Analysis for Existing Projects',
    note: 'Model budget scenarios for projects that already exist in Redmine. Pick a live project to try out changes to its team, allocations and costs — nothing you do here affects the real project.',
  },
  {
    id: 'opportunities',
    label: 'What-If Analysis for New Opportunities',
    note: "Plan budgets for prospective work that isn't in Redmine yet. Create an opportunity, build a hypothetical team, and estimate what it would cost before the work is won.",
  },
];

const WhatIfAnalysisPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const editId = searchParams.get('edit');
  // Edit a saved opportunity scenario (opportunities never use saved dashboards).
  const opportunityScenarioId = searchParams.get('opportunityScenario');
  // Set when arriving from the Opportunities list.
  const opportunityId = searchParams.get('opportunity');
  const shouldAddMembers = searchParams.get('addMembers') === '1';
  
  // What the scenario is for: a Redmine project OR an opportunity, never both.
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [selectedOpportunity, setSelectedOpportunity] = useState<Opportunity | null>(null);
  const [projectCosts, setProjectCosts] = useState<ProjectCost[]>([]);
  const [allocationMembers, setAllocationMembers] = useState<AllocationMember[]>([]);
  const [memberCosts, setMemberCosts] = useState<MemberCosts>({});
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  // Saved dashboard (project) or saved scenario (opportunity) being edited. For an
  // opportunity it is also set after the first save, so later saves update it.
  const [editingDashboardId, setEditingDashboardId] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>('allocation');
  const [scenarioTab, setScenarioTab] = useState<ScenarioTab>('existing');
  const [showNewOpportunityModal, setShowNewOpportunityModal] = useState<boolean>(false);
  // Quote margin and the resulting price; saved alongside the scenario.
  const [profitability, setProfitability] = useState<{
    profitPercent: number;
    revenue: number | null;
  }>({ profitPercent: DEFAULT_PROFIT_PERCENT, revenue: null });
  // Set right after an opportunity is created so the grid opens its member picker.
  const [autoOpenAddMember, setAutoOpenAddMember] = useState<boolean>(false);
  // Costs tab mounts on first visit and then stays mounted, so switching tabs
  // never discards what has been entered.
  const [hasOpenedCostsTab, setHasOpenedCostsTab] = useState<boolean>(false);
  const costFetchKeyRef = useRef<string>('');

  const scope = useMemo<WorkScope | null>(() => {
    if (selectedProject) return { kind: 'project', project: selectedProject };
    if (selectedOpportunity) return { kind: 'opportunity', opportunity: selectedOpportunity };
    return null;
  }, [selectedProject, selectedOpportunity]);
  const scopeKey = workScopeKey(scope);
  const workName = selectedProject?.name ?? selectedOpportunity?.name ?? '';
  const workIdLine = selectedProject
    ? `ID: ${selectedProject.id} • Identifier: ${selectedProject.identifier || 'N/A'}`
    : selectedOpportunity
      ? `Opportunity #${selectedOpportunity.id}`
      : '';
  const endDate = selectedProject?.updated_on || selectedOpportunity?.updated_at || '';

  useEffect(() => {
    if (activeTab === 'costs') {
      setHasOpenedCostsTab(true);
    }
  }, [activeTab]);

  // Switching the outer tab starts a fresh scenario: a project picked under one
  // tab must not carry its team and costs over into the other.
  const handleScenarioTabChange = useCallback((tab: ScenarioTab) => {
    if (tab === scenarioTab) {
      return;
    }
    setScenarioTab(tab);
    setSelectedProject(null);
    setSelectedOpportunity(null);
    setEditingDashboardId(null);
    setProjectCosts([]);
    setAllocationMembers([]);
    setMemberCosts({});
    setActiveTab('allocation');
    setAutoOpenAddMember(false);
    setProfitability({ profitPercent: DEFAULT_PROFIT_PERCENT, revenue: null });
    costFetchKeyRef.current = '';
  }, [scenarioTab]);

  // A new opportunity has no members yet, so hand the user straight to the
  // same "Add Member" popup the allocation grid uses.
  const handleOpportunityCreated = useCallback((opportunity: Opportunity) => {
    setShowNewOpportunityModal(false);
    setSelectedProject(null);
    setSelectedOpportunity(opportunity);
    setEditingDashboardId(null);
    setScenarioTab('opportunities');
    setActiveTab('allocation');
    setAutoOpenAddMember(true);
  }, []);

  // Memoize the callback to prevent infinite loops
  const handleAllocationChange = useCallback((members: AllocationMember[]) => {
    setAllocationMembers(members);
  }, []);

  // Handle project costs change from WhatIfAllocationGrid
  const handleProjectCostsChange = useCallback((costs: ProjectCost[]) => {
    setProjectCosts(costs);
  }, []);

  // Reset project costs and allocations when the project/opportunity changes (unless in edit mode)
  useEffect(() => {
    if (scopeKey && !isEditing) {
      setProjectCosts([]);
      setAllocationMembers([]);
    }
  }, [scopeKey, isEditing]);

  // Load a saved project dashboard or opportunity scenario for editing
  useEffect(() => {
    if (editId) {
      loadDashboardForEdit(parseInt(editId));
    } else if (opportunityScenarioId) {
      loadOpportunityScenarioForEdit(parseInt(opportunityScenarioId));
    }
  }, [editId, opportunityScenarioId]);

  // Preselect an opportunity handed over from the Opportunities list, so the
  // user lands directly on its analysis.
  useEffect(() => {
    if (!opportunityId || editId || opportunityScenarioId) {
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const opportunity = await opportunityService.getOpportunity(parseInt(opportunityId));
        if (cancelled) return;
        setSelectedProject(null);
        setSelectedOpportunity(opportunity);
        setScenarioTab('opportunities');
        setActiveTab('allocation');
        if (shouldAddMembers) {
          setAutoOpenAddMember(true);
        }
      } catch (err) {
        if (!cancelled) {
          alert(err instanceof Error ? err.message : 'Failed to load opportunity');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [opportunityId, editId, opportunityScenarioId, shouldAddMembers]);

  // Put a saved scenario's figures back on the page - shared by project dashboards
  // and opportunity scenarios, which store the same figures.
  const restoreSavedFigures = (
    savedId: number,
    data: Pick<DashboardData, 'calculatedCosts' | 'projectCosts' | 'allocationMembers'>
  ) => {
    // Restore the saved quote. Older dashboards have neither field, so they
    // fall back to the default percent and a revenue derived from it.
    setProfitability({
      profitPercent: data.calculatedCosts?.profitPercent ?? DEFAULT_PROFIT_PERCENT,
      revenue: data.calculatedCosts?.revenue ?? null,
    });

    // Load saved project costs (handle old and new format)
    if (data.projectCosts && Array.isArray(data.projectCosts) && data.projectCosts.length > 0) {
      const monthsForConversion = generateMonths(new Date().toISOString().split('T')[0], 12);
      const convertedCosts = data.projectCosts.map((cost: any) => {
        if (cost.monthlyCost !== undefined && !cost.monthlyCosts) {
          return {
            ...cost,
            monthlyCosts: monthsForConversion.reduce((acc, month) => ({
              ...acc,
              [month.monthStart]: cost.monthlyCost || 0,
            }), {}),
          };
        }
        // Ensure monthlyCosts exists and is an object
        if (!cost.monthlyCosts || typeof cost.monthlyCosts !== 'object') {
          return {
            ...cost,
            monthlyCosts: monthsForConversion.reduce((acc, month) => ({
              ...acc,
              [month.monthStart]: 0,
            }), {}),
          };
        }
        return cost;
      });
      setProjectCosts(convertedCosts);
    } else {
      setProjectCosts([]);
    }

    setAllocationMembers(data.allocationMembers || []);
    setIsEditing(true);
    setEditingDashboardId(savedId);
  };

  const loadOpportunityScenarioForEdit = async (scenarioId: number) => {
    try {
      const scenario = await opportunityService.getScenario(scenarioId);
      // Only active opportunities can be edited; this fails for a deleted one.
      const opportunity = await opportunityService.getOpportunity(scenario.opportunityId);
      setSelectedProject(null);
      setSelectedOpportunity(opportunity);
      setScenarioTab('opportunities');
      restoreSavedFigures(scenarioId, scenario);
    } catch (err) {
      console.error('Error loading opportunity scenario:', err);
      alert(`Failed to load opportunity scenario for editing: ${err instanceof Error ? err.message : ''}`);
    }
  };

  const loadDashboardForEdit = async (dashboardId: number) => {
    console.log("Loading WhatIf dashboard for edit:", dashboardId);
    try {
      const data = await DashboardService.getDashboardById(dashboardId);
      if (!data) {
        throw new Error('Dashboard not found');
      }

      // Fetch full project details
      let projectDetails = await projectService.getProjectById(data.projectId);
      if (!projectDetails) {
        projectDetails = {
          id: data.projectId,
          name: data.projectName || `Project ${data.projectId}`,
          identifier: `project-${data.projectId}`,
          status: 1,
          is_public: 0,
          inherit_members: 0,
          created_on: new Date().toISOString(),
          updated_on: new Date().toISOString(),
        } as Project;
      }

      setSelectedOpportunity(null);
      setSelectedProject(projectDetails);
      setScenarioTab('existing');
      restoreSavedFigures(dashboardId, data);
    } catch (err) {
      console.error('Error loading WhatIf dashboard:', err);
      alert('Failed to load WhatIf scenario for editing');
    }
  };

  // Generate months for cost summary
  const months = scope ? generateMonths(new Date().toISOString().split('T')[0], 12) : [];
  const monthKeySignature = useMemo(() => {
    if (!months.length) {
      return '';
    }
    return months.map(m => m.monthStart.substring(0, 7)).join(',');
  }, [months]);
  const memberIdsSignature = useMemo(() => {
    if (!allocationMembers.length) {
      return '';
    }
    const ids = allocationMembers.map(m => m.id).sort((a, b) => a - b);
    return ids.join(',');
  }, [allocationMembers]);
  // Fetch month-specific costs when allocationMembers change
  useEffect(() => {
    if (!scopeKey || !memberIdsSignature || !monthKeySignature) {
      if (!memberIdsSignature && costFetchKeyRef.current) {
        setMemberCosts({});
        costFetchKeyRef.current = '';
      }
      return;
    }

    const fetchKey = `${scopeKey}|${memberIdsSignature}|${monthKeySignature}`;
    if (fetchKey === costFetchKeyRef.current) {
      return;
    }
    costFetchKeyRef.current = fetchKey;

    const memberIds = memberIdsSignature.split(',').map(id => Number(id)).filter(id => !Number.isNaN(id));
    const monthKeys = monthKeySignature.split(',').filter(Boolean);

    if (!memberIds.length || !monthKeys.length) {
      return;
    }

    console.log('💰 Fetching month-specific costs for WhatIf CostSummary...');
    MemberCostService.getMemberCostsByMonths(memberIds, monthKeys)
      .then(costs => {
        console.log('✅ Member costs loaded for WhatIf CostSummary:', costs);
        setMemberCosts(costs);
      })
      .catch(error => {
        console.error('❌ Error loading member costs:', error);
      });
  }, [scopeKey, memberIdsSignature, monthKeySignature]);

  // Rate a member's hours are costed at - the TMIS employee cost rate
  const getMemberCostForMonth = (memberId: number, monthStart: string): number => {
    const member = allocationMembers.find(m => m.id === memberId);
    return getMemberRateForMonth(member, memberCosts, monthStart);
  };

  const calculateTotals = () => {
    const totalEmployeeCosts = allocationMembers.reduce((total, member) => {
      return total + months.reduce((memberTotal, month) => {
        const hours = member.monthlyAllocations[month.monthStart] || 0;
        const costForMonth = getMemberCostForMonth(member.id, month.monthStart);
        return memberTotal + (hours * costForMonth);
      }, 0);
    }, 0);

    const billableCosts = projectCosts.filter(cost => cost.billable)
      .reduce((total, cost) => {
        const costTotal = Object.values(cost.monthlyCosts).reduce((sum, val) => sum + (val || 0), 0);
        return total + costTotal;
      }, 0);
    const nonBillableCosts = projectCosts.filter(cost => !cost.billable)
      .reduce((total, cost) => {
        const costTotal = Object.values(cost.monthlyCosts).reduce((sum, val) => sum + (val || 0), 0);
        return total + costTotal;
      }, 0);
    const totalProjectCosts = billableCosts + nonBillableCosts;
    const totalCost = totalEmployeeCosts + totalProjectCosts;

    return {
      totalEmployeeCosts,
      totalProjectCosts,
      totalCost,
      billableCosts,
      nonBillableCosts
    };
  };

  const calculateMonthlyBreakdown = () => {
    return months.map(month => {
      const employeeCosts = allocationMembers.reduce((total, member) => {
        const hours = member.monthlyAllocations[month.monthStart] || 0;
        const costForMonth = getMemberCostForMonth(member.id, month.monthStart);
        return total + (hours * costForMonth);
      }, 0);

      const projectCostsForMonth = projectCosts.reduce((total, cost) => {
        return total + (cost.monthlyCosts[month.monthStart] || 0);
      }, 0);

      const totalHours = allocationMembers.reduce((total, member) => {
        return total + (member.monthlyAllocations[month.monthStart] || 0);
      }, 0);

      return {
        monthStart: month.monthStart,
        monthLabel: month.label,
        employeeCosts,
        projectCosts: projectCostsForMonth,
        totalCost: employeeCosts + projectCostsForMonth,
        totalHours
      };
    });
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditingDashboardId(null);
    setSelectedProject(null);
    setSelectedOpportunity(null);
    setProjectCosts([]);
    setAllocationMembers([]);
    
    window.location.href = '/whatif';
  };

  const handleSaveData = async () => {
    if (!selectedProject && !selectedOpportunity) {
      alert('Please select a project first');
      return;
    }

    setIsSaving(true);
    try {
      const baseCosts = calculateTotals();
      const calculatedCosts = {
        ...baseCosts,
        profitPercent: profitability.profitPercent,
        // Not yet seeded (e.g. saved before the summary rendered) - derive it.
        revenue:
          profitability.revenue ??
          round2(revenueFromProfitPercent(baseCosts.totalCost, profitability.profitPercent)),
      };
      const monthlyBreakdown = calculateMonthlyBreakdown();

      // Filter out invalid or empty members before saving
      const validAllocationMembers = allocationMembers
        .filter(member => 
          member.member && 
          member.member.id && 
          member.member.firstname && 
          member.member.lastname &&
          !member.removed
        )
        .filter((member, index, arr) => 
          arr.findIndex(m => m.member.id === member.member.id) === index
        );

      const figures = {
        label: `WhatIf: ${workName}`,
        allocationMembers: validAllocationMembers,
        projectCosts,
        calculatedCosts,
        monthlyBreakdown,
        savedAt: new Date().toISOString()
      };

      // Opportunities save to their own scenarios, never to project dashboards
      if (selectedOpportunity) {
        const result = editingDashboardId
          ? await opportunityService.updateScenario(editingDashboardId, figures)
          : await opportunityService.saveScenario(selectedOpportunity.id, figures);
        // Later saves in this session update the same scenario
        setEditingDashboardId(result.id);
        alert(`Opportunity scenario ${editingDashboardId ? 'updated' : 'saved'} successfully! Scenario ID: ${result.id}`);
        return;
      }

      // Redmine projects save to dashboard_data
      const dashboardData = { projectId: selectedProject!.id, ...figures };

      if (isEditing && editingDashboardId) {
        // Update existing dashboard
        const result = await DashboardService.saveDashboardData(dashboardData);
        alert(`WhatIf scenario updated successfully! Dashboard ID: ${result.id}`);
        console.log('WhatIf scenario updated:', result);
      } else {
        // Create new dashboard
        const result = await DashboardService.saveDashboardData(dashboardData);
        alert(`WhatIf scenario saved successfully! Dashboard ID: ${result.id}`);
        console.log('WhatIf scenario saved:', result);
      }
      
    } catch (error) {
      console.error('Error saving WhatIf scenario:', error);
      alert(`Failed to save WhatIf scenario: ${error instanceof Error ? error.message : 'Please try again.'}`);
    } finally {
      setIsSaving(false);
    }
  };

  // What is being edited: an opportunity or a Redmine project. Falls back to the
  // outer tab before it has loaded, so the heading never flickers.
  const editingOpportunity = scope
    ? scope.kind === 'opportunity'
    : scenarioTab === 'opportunities';

  return (
    <PermissionGuard
      pbsRole="project_owner"
      fallback={
        <AccessDenied
          title="Project Owner Access Required"
          message="Only PBS-approved project owners can create or modify What-If scenarios."
        />
      }
    >
      <div className="space-y-6 bg-gradient-to-br from-white via-purple-50/40 to-pink-50/30 rounded-xl p-6 shadow-sm border border-purple-100/50">
      {/* What-If Scenario Banner */}
      <div className="bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg p-4">
        <div className="flex items-center">
          <svg className="h-6 w-6 text-purple-600 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
          </svg>
          <div>
            <h3 className="text-sm font-medium text-purple-800">What-If Analysis Mode</h3>
            <p className="mt-1 text-sm text-purple-700">
              Create and analyze different project scenarios. Add or remove team members, adjust allocations, and save scenarios for future reference.
            </p>
          </div>
        </div>
      </div>

      {/* Editing Mode Banner */}
      {isEditing && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <svg className="h-5 w-5 text-blue-600 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="text-sm font-medium text-blue-800">
                    Editing {workTypeLabel(editingOpportunity)} Scenario
                  </h3>
                  <WorkTypeBadge isOpportunity={editingOpportunity} />
                </div>
                <p className="mt-1 text-sm text-blue-700">
                  You are editing {editingOpportunity ? 'Scenario' : 'Dashboard'} ID: {editingDashboardId}
                  {scope ? ` for ${workTypeLabel(editingOpportunity).toLowerCase()} "${workName}"` : ''}.
                  {' '}Click "Save Scenario" to save changes or "Cancel" to discard.
                </p>
              </div>
            </div>
            <button
              onClick={handleCancelEdit}
              className="px-3 py-1.5 text-sm font-medium text-blue-700 hover:text-blue-800 hover:bg-blue-100 rounded-md transition-colors"
            >
              Cancel Edit
            </button>
          </div>
        </div>
      )}

      {/* Page Header */}
      <div className="border-b border-gray-200 pb-4">
        <h2 className="text-2xl font-bold text-gray-900">
          {isEditing ? `Edit What-If Scenario for ${workTypeLabel(editingOpportunity)}` : 'What-If Analysis'}
        </h2>
        <p className="mt-1 text-sm text-gray-600">Create and analyze different project scenarios with custom team compositions</p>
      </div>

      {/* Scenario Tabs - hidden while editing, where the project is locked */}
      {!isEditing && (
        <div className="space-y-3">
          <div className="inline-flex flex-wrap gap-1 p-1 bg-gray-100 rounded-lg" role="tablist" aria-label="What-If analysis mode">
            {SCENARIO_TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={scenarioTab === tab.id}
                onClick={() => handleScenarioTabChange(tab.id)}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-1 ${
                  scenarioTab === tab.id
                    ? 'bg-white text-purple-700 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* What this tab is for */}
          <div className="flex items-start justify-between gap-4 bg-purple-50/60 border border-purple-100 rounded-lg p-3">
            <p className="text-sm text-purple-800">
              {SCENARIO_TABS.find((tab) => tab.id === scenarioTab)?.note}
            </p>
            {scenarioTab === 'opportunities' && (
              <button
                type="button"
                onClick={() => setShowNewOpportunityModal(true)}
                className="inline-flex flex-shrink-0 items-center px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-1 transition-all"
              >
                <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                New Opportunity
              </button>
            )}
          </div>
        </div>
      )}

      {/* Project Selection Component */}
      {isEditing && scope ? (
        <div className="bg-white rounded-lg shadow-sm border border-blue-100 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">
                {workTypeLabel(editingOpportunity)} locked during edit
              </p>
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-semibold text-gray-900">{workName}</h3>
                <WorkTypeBadge isOpportunity={editingOpportunity} />
              </div>
              <p className="text-sm text-gray-500">{workIdLine}</p>
            </div>
            <div className="text-sm text-blue-700 font-medium">
              Create a new scenario to change the {workTypeLabel(editingOpportunity).toLowerCase()}.
            </div>
          </div>
        </div>
      ) : scenarioTab === 'opportunities' ? (
        /*
         * Opportunities are created here, not picked from a list: the only way
         * into an opportunity scenario is to make a new one. Existing ones are
         * reopened from the Opportunities list or a saved scenario's edit link.
         */
        selectedOpportunity ? (
          <div className="bg-white rounded-lg shadow-sm border border-purple-100 p-4">
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-sm text-gray-500">Building a scenario for</p>
                <div className="flex items-center gap-2">
                  <h3 className="text-lg font-semibold text-gray-900">{selectedOpportunity.name}</h3>
                  <WorkTypeBadge isOpportunity />
                </div>
                <p className="text-sm text-gray-500">{workIdLine}</p>
              </div>
              <button
                type="button"
                onClick={() => setShowNewOpportunityModal(true)}
                className="inline-flex flex-shrink-0 items-center px-4 py-2 border border-purple-200 text-purple-700 text-sm font-medium rounded-md hover:bg-purple-50 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-1 transition-all"
              >
                Start another opportunity
              </button>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm border border-purple-100 p-8 text-center">
            <h3 className="text-lg font-semibold text-gray-900">No opportunity yet</h3>
            <p className="mt-1 text-sm text-gray-600">
              Create one to start costing prospective work that isn't in Redmine yet.
            </p>
            <button
              type="button"
              onClick={() => setShowNewOpportunityModal(true)}
              className="mt-4 inline-flex items-center px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-1 transition-all"
            >
              <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              New Opportunity
            </button>
          </div>
        )
      ) : (
        <ProjectSelector
          selectedProject={selectedProject}
          onProjectChange={setSelectedProject}
          onSubmit={() => {}}
        />
      )}

      {/* Tabbed Content - Only show when a project or opportunity is selected */}
      {scope && (
        <div className="bg-white/80 backdrop-blur-sm rounded-lg shadow-sm border border-purple-100/50 overflow-hidden">
          {/* Tab Navigation */}
          <div className="border-b border-gray-200 bg-gray-50/50">
            <nav className="flex -mb-px" aria-label="Tabs">
              <button
                onClick={() => setActiveTab('allocation')}
                className={`
                  flex-1 px-6 py-4 text-sm font-medium text-center border-b-2 transition-colors
                  ${activeTab === 'allocation'
                    ? 'border-purple-500 text-purple-600 bg-purple-50'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 hover:bg-gray-50'
                  }
                `}
                aria-label="Team Allocation tab"
                aria-selected={activeTab === 'allocation'}
                role="tab"
              >
                <div className="flex items-center justify-center space-x-2">
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                  <span>Step 2: Team Allocation</span>
                  {allocationMembers.length > 0 && (
                    <span className="ml-2 px-2 py-0.5 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                      {allocationMembers.length}
                    </span>
                  )}
                </div>
              </button>
              <button
                onClick={() => setActiveTab('costs')}
                className={`
                  flex-1 px-6 py-4 text-sm font-medium text-center border-b-2 transition-colors
                  ${activeTab === 'costs'
                    ? 'border-purple-500 text-purple-600 bg-purple-50'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 hover:bg-gray-50'
                  }
                `}
                aria-label="Project Costs tab"
                aria-selected={activeTab === 'costs'}
                role="tab"
              >
                <div className="flex items-center justify-center space-x-2">
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>Step 3: Project Costs</span>
                  {projectCosts.length > 0 && (
                    <span className="ml-2 px-2 py-0.5 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                      {projectCosts.length}
                    </span>
                  )}
                </div>
              </button>
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {/* Both tabs stay mounted once opened - unmounting them would destroy the
                grid's in-progress members/hours and the cost rows being edited. */}
            <div className={activeTab === 'allocation' ? 'space-y-4' : 'hidden'}>
              <div className="space-y-4">
                <div className="mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Assign Hours to Team Members</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    {workTypeLabel(editingOpportunity)}: <span className="font-medium">{workName}</span> ({workIdLine})
                  </p>
                  <p className="text-xs text-gray-500 mt-2">
                    💡 Add or remove team members and assign hours per month. Maximum 160 hours per month per member.
                  </p>
                </div>
                <WhatIfAllocationGrid
                  scope={scope}
                  onAllocationChange={handleAllocationChange}
                  onProjectCostsChange={handleProjectCostsChange}
                  initialMembers={isEditing ? allocationMembers : undefined}
                  autoOpenAddMember={autoOpenAddMember}
                  onAutoOpenHandled={() => setAutoOpenAddMember(false)}
                />
              </div>
            </div>

            {hasOpenedCostsTab && (
              <div className={activeTab === 'costs' ? 'space-y-4' : 'hidden'}>
                <div className="mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Add Project Expenses</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Enter additional project costs such as travel, software licenses, hardware, etc.
                  </p>
                  <p className="text-xs text-gray-500 mt-2">
                    💡 Mark costs as billable if they should be included in client billing.
                  </p>
                </div>
                <ProjectCosts
                  key={`project-costs-${isEditing ? editingDashboardId : scopeKey}-${projectCosts.length}`}
                  costs={projectCosts}
                  months={months}
                  onCostsChange={setProjectCosts}
                />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Cost Summary Component */}
      {scope && allocationMembers.length > 0 && (
        <CostSummary
          allocationMembers={allocationMembers}
          months={months}
          projectCosts={projectCosts}
          memberCosts={memberCosts}
          endDate={endDate}
          profitPercent={profitability.profitPercent}
          revenue={profitability.revenue}
          onProfitabilityChange={setProfitability}
        />
      )}

      {/* Charts Component */}
      {scope && allocationMembers.length > 0 && (
        <Charts
          allocationMembers={allocationMembers}
          months={months}
          projectCosts={projectCosts}
          endDate={endDate}
        />
      )}

               {!scope && scenarioTab === 'existing' && (
                 <div className="bg-white/80 backdrop-blur-sm rounded-lg shadow p-6 border border-purple-100/50">
          <div className="text-center py-12">
            <div className="mx-auto h-12 w-12 text-gray-400">
              <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
            </div>
            <h3 className="mt-2 text-sm font-medium text-gray-900">Select a Project</h3>
            <p className="mt-1 text-sm text-gray-500">Choose a project above to start creating your What-If scenario.</p>
          </div>
        </div>
      )}

      {/* Sticky Save/Cancel Footer */}
      {scope && allocationMembers.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-600">
                {isEditing ? (
                  <span className="flex items-center">
                    <svg className="h-4 w-4 text-blue-600 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    Editing WhatIf Scenario ID: {editingDashboardId}
                  </span>
                ) : (
                  <span>Ready to save WhatIf scenario</span>
                )}
              </div>
              <div className="flex items-center gap-3">
                {isEditing && (
                  <button
                    onClick={handleCancelEdit}
                    className="px-6 py-2.5 bg-white text-gray-700 text-sm font-medium rounded-md border border-gray-300 hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                )}
                <button
                  onClick={handleSaveData}
                  disabled={isSaving}
                  className="px-6 py-2.5 bg-purple-600 text-white text-sm font-medium rounded-md hover:bg-purple-700 disabled:bg-purple-400 disabled:cursor-not-allowed flex items-center gap-2 shadow-sm"
                >
                  {isSaving ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      {isEditing ? 'Saving...' : 'Saving...'}
                    </>
                  ) : (
                    <>
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3-3m0 0l-3 3m3-3v12" />
                      </svg>
                      {isEditing ? 'Update Scenario' : 'Save Scenario'}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add padding at bottom to prevent content from being hidden by sticky footer */}
      {scope && allocationMembers.length > 0 && (
        <div className="h-20"></div>
      )}

      <NewOpportunityModal
        isOpen={showNewOpportunityModal}
        onClose={() => setShowNewOpportunityModal(false)}
        onCreated={handleOpportunityCreated}
      />
    </div>
    </PermissionGuard>
  );
};

export default WhatIfAnalysisPage;

